import type { MapTriggerVolume } from '../../content/schema/map';
import { dispatchTrigger, type TriggerContext } from '../../content/registry/triggerRegistry';
import type { LogicAction, LogicDocument, QuestStatus } from './contract';
import { LogicEvaluator, type LogicEffect, type LogicEvent, type LogicFacts } from './evaluator';
import { diffTriggerContainment, triggerLabel } from './triggerEvents';

/** A `logic-snapshot` message's shared-state payload, restorable via
 * `applyRemoteSnapshot` - see that method's own doc comment. Matches
 * `LogicSnapshotSyncMessage`'s own `variables`/`quests`/`outcome` fields
 * (syncMessage.ts) without importing it directly: that type lives in
 * `src/studio/play/relay`, outside the engine (this file's own package),
 * and the engine must not import game/app code (check-engine-boundary.mjs).
 * A structurally-identical shape here is deliberately not a *shared* type
 * with the wire one - the two are allowed to diverge if either side's needs
 * ever do, same as `ClientLogicTickPoint` already stays its own shape
 * rather than importing whatever the map schema itself calls a point. */
export interface RemoteLogicSnapshot {
  variables: Record<string, number>;
  quests: Record<string, { status: QuestStatus; title?: string }>;
  /** Every player's item counts, by player id then item name - added
   * alongside `variables`/`quests` so a late-joining client's item-count
   * state is caught up too, the same as those two already were. */
  items: Record<string, Record<string, number>>;
  outcome: { result: 'win' | 'lose'; team?: string } | null;
}

/** A single-player session has no authored starting health today (there is
 * no Game Setup field for it) - seeded here, once, as a `set-variable` rule
 * ahead of the creator's own document, so `variable('health')` reads a
 * sensible non-zero default the first time the HUD asks for it instead of
 * an empty-looking 0. A creator's own round-start rule (if they author one)
 * still runs after this and can override it, same as any other rule
 * ordering. Nothing else is seeded - `ammo` defaulting to 0 (no seed rule
 * needed) already reads as "nothing given yet", which is the honest state. */
const DEFAULT_SEED_RULES: LogicDocument['rules'] = [
  {
    id: '__seed-health',
    when: 'round-start',
    then: [{ kind: 'set-variable', name: 'health', value: 100 }],
  },
];

/** Fallback local-player id for a single-player/no-relay session (Preview,
 * a published game with no relay deployed, or before a relay connection has
 * actually opened) - never a real relay-issued player id, just a stable
 * placeholder so every effect/fact still has a non-empty `playerId` to key
 * off. See `ClientLogicRuntimeOptions.localPlayerId`. */
const LOCAL_PLAYER_ID_FALLBACK = 'local';

/** Reserved `ruleId` for an effect applied via `applyAction`, tagging it as
 * script-triggered (the HollowScript `logic` capability) rather than fired
 * by an authored rule matching an event. */
const SCRIPT_RULE_ID = '__script';

/** Reserved `ruleId` for an effect applied via `applyRemoteAction`, tagging
 * it as sourced from another client over the relay (a host's broadcast
 * `logic-effect` message, once the sync engine's Phase 3 wires it up) rather
 * than fired by local rule matching or the scripting capability. See
 * `roadmap.md` section 2.6's "natural extension point" and section 3.2 item
 * 3 - this constant and `applyRemoteAction` are the extension point itself;
 * nothing yet calls them (that is Phase 3's relay-listening work). */
const REMOTE_RULE_ID = '__remote';

export interface ClientLogicTickPoint {
  x: number;
  z: number;
  floor: number;
}

export interface ClientLogicRuntimeOptions {
  document: LogicDocument;
  /** Called once for every effect the moment it's produced (announce feed,
   * item pickups, custom emits) - a host wires this to its HUD feed rather
   * than polling `effectsSince`. */
  onEffect?: (effect: LogicEffect) => void;
  /**
   * The real relay-issued player id for this session's local player
   * (`playerSession.ts`'s cached guest id), when a relay connection exists.
   * Every event/effect this runtime produces is stamped with this id - two
   * real players sharing a session must not have their actions attributed
   * to the same id. Omitted (or empty) falls back to
   * `LOCAL_PLAYER_ID_FALLBACK` for a single-player/no-relay session, where
   * there is only ever one local player and no relay identity to use. Can
   * be changed after construction via `setLocalPlayerId` once a relay
   * connection that was not yet open at construction time opens.
   */
  localPlayerId?: string;
  /**
   * The real connected-player count from relay presence, when known.
   * Omitted (or non-positive) falls back to `1` for a single-player/no-relay
   * session. Drives `LogicFacts.playerCount`, so a "wait for 2 players"
   * start condition or a scaling difficulty rule can actually fire
   * correctly. Presence changes over a session's lifetime as players join
   * and leave, so update it live via `setPlayerCount` rather than only at
   * construction.
   */
  playerCount?: number;
  /**
   * Whether this client is the session's elected Logic host (roadmap
   * section 3.2 - a deputized listen-server client, not a real server).
   * Defaults to `true`: a single-player/no-relay session has exactly one
   * client and is trivially its own host, so omitting this entirely
   * preserves single-player/Preview behaviour byte-for-byte. Once a relay
   * connection resolves a real election result, drive it live via
   * `setIsHost` (same pattern as `localPlayerId`/`playerCount`) rather than
   * recreating the runtime.
   *
   * What this actually gates: only while `isHost` is true does `tick()`
   * fire the two global-lifecycle event kinds this contract has rules for -
   * `round-start` (once per session, exactly when - see `tick()`'s own doc
   * comment, since "once" needs real care in a multiplayer session) and
   * the periodic `tick` event - so only the host evaluates those two rule
   * kinds at all. Every effect this runtime produces *while `isHost`* -
   * whichever event or call site produced it: round-start, tick,
   * trigger-enter/exit, or a script-triggered `applyAction` call - also
   * reaches `onSharedEffect` (see `report()`'s own doc comment for why this
   * is deliberately not narrowed to just round-start/tick). A non-host
   * client still fires `player-join`/`trigger-enter`/`trigger-exit` locally
   * exactly as before, but never reaches `onSharedEffect` for them (`report()`
   * gates purely on `isHost`) - it instead receives the host's shared
   * effects over the relay and replays them via `applyRemoteAction`/
   * `applyRemoteSnapshot` - wiring that relay listener is the studio layer's
   * job at the call site, not this class's.
   */
  isHost?: boolean;
  /**
   * Host-only: called for every effect this runtime produces while `isHost`
   * - i.e. exactly the effects a Logic host should broadcast over the relay
   * as a `logic-effect` message (roadmap 3.2 item 2/3.4), via the shared
   * `report()` choke point (see its own doc comment) - a strict subset of
   * what `onEffect` already receives (every effect, host or not). Never
   * called while `isHost` is false.
   */
  onSharedEffect?: (effect: LogicEffect) => void;
}

/** The read-only surface a host (Studio's HUD) is handed via a ref - `tick`
 * and `end` stay off this type since only `GameRuntime` itself, which owns
 * the per-frame loop, should drive the session forward. */
export interface ClientLogicRuntimeHandle {
  variable(name: string): number;
  itemCount(name: string): number;
  questStatus(questId: string): QuestStatus;
  questTitle(questId: string): string | undefined;
  outcome(): { result: 'win' | 'lose'; team?: string } | null;
  readonly log: readonly LogicEffect[];
}

/** How often (ms) the session's own `'tick'` Logic event fires - see
 * `LOGIC_EVENT_KINDS` in contract.ts for what it is for. Independent of
 * frame rate: `tick()` below is called every rendered frame, but this event
 * is throttled by wall-clock time so a rule authored against it fires at a
 * predictable, frame-rate-independent cadence. */
const TICK_EVENT_INTERVAL_MS = 1000;

/**
 * Drives a `LogicEvaluator` for the generic, single-player client-side play
 * and preview path - the counterpart to `sstk-sunder-realtime`'s
 * `LogicDriver`, which derives events from an authoritative match snapshot.
 * This one has no snapshot to diff: it derives its own events directly from
 * a per-tick player position (real trigger-volume containment, respecting
 * floor) and a simple once-only session lifecycle (`player-join` then
 * `round-start` on the first tick). `round-end`/`score-change` are part of
 * the shared contract but nothing in this host computes them yet - a
 * creator's rules for those events simply do not fire outside a real match
 * lifecycle (today, only `sstk-sunder-realtime` has one).
 *
 * Trigger dispatch is two independent, deliberately separate mechanisms
 * that both fire on the same containment change: `dispatchTrigger` (from
 * `triggerRegistry`) runs whatever mechanical handler a kind has registered
 * - built-in or game-registered - and the Logic evaluator runs whatever
 * `trigger-enter`/`trigger-exit` rules the creator authored. Neither knows
 * about the other; a kind with no registered handler still reaches Logic,
 * and a Logic rule reacting to a trigger does not require a handler to
 * exist.
 */
export class ClientLogicRuntime implements ClientLogicRuntimeHandle {
  private readonly evaluator: LogicEvaluator;
  private readonly onEffect?: (effect: LogicEffect) => void;
  private readonly onSharedEffect?: (effect: LogicEffect) => void;
  private readonly insideIds = new Set<string>();
  private readonly effects: LogicEffect[] = [];
  private tickCount = 0;
  private started = false;
  /** Whether `round-start` has fired for this session yet - either locally
   * (this client was host on the tick that first found `isHost` true), or
   * because a remote shared-state broadcast proved some other client's own
   * `round-start` already has (`applyRemoteAction`/`applyRemoteSnapshot`
   * both set this true). See `tick()`'s own doc comment for the full
   * reasoning - this is deliberately a separate flag from `started`. */
  private roundStarted = false;
  private lastTickEventAt = 0;
  private localPlayerId: string;
  private playerCount: number;
  private isHost: boolean;

  constructor(options: ClientLogicRuntimeOptions) {
    this.evaluator = new LogicEvaluator({
      version: options.document.version,
      rules: [...DEFAULT_SEED_RULES, ...options.document.rules],
    });
    this.onEffect = options.onEffect;
    this.onSharedEffect = options.onSharedEffect;
    this.localPlayerId = options.localPlayerId || LOCAL_PLAYER_ID_FALLBACK;
    this.playerCount = options.playerCount && options.playerCount > 0 ? options.playerCount : 1;
    this.isHost = options.isHost ?? true;
  }

  /** Update whether this client is the session's elected Logic host, live -
   * see `ClientLogicRuntimeOptions.isHost`'s own doc comment for what this
   * gates and why it defaults `true`. Call whenever host election's result
   * changes (relay presence changing, or a relay connection opening/closing
   * for the first time) rather than recreating the runtime, same pattern as
   * `setLocalPlayerId`/`setPlayerCount`. */
  setIsHost(isHost: boolean): void {
    this.isHost = isHost;
  }

  /** Update the id stamped onto every event/effect produced from this point
   * on, e.g. once a relay connection that was not yet open at construction
   * time opens and the real relay-issued id becomes known. Does not
   * retroactively relabel effects already in `log`. A falsy id is ignored
   * (stays on whatever id was set before), so a caller can pass a possibly-
   * unresolved value without an explicit guard. */
  setLocalPlayerId(playerId: string): void {
    if (!playerId) return;
    this.localPlayerId = playerId;
  }

  /** Update the connected-player count `LogicFacts.playerCount` reports from
   * this point on - call whenever relay presence changes (players joining or
   * leaving over the session's lifetime). Clamped to at least 1: a session
   * always has its own local player, even if presence briefly reports 0
   * during a reconnect. */
  setPlayerCount(count: number): void {
    this.playerCount = count > 0 ? count : 1;
  }

  /** Current value of a Logic variable (e.g. `'health'`); unset reads as 0. */
  variable(name: string): number {
    return this.evaluator.variable(name);
  }

  /** The local player's count of a Game Data item; unseen reads as 0. */
  itemCount(name: string): number {
    return this.evaluator.itemCount(this.localPlayerId, name);
  }

  /** A named quest's current status; never started reads as 'inactive'. */
  questStatus(questId: string): QuestStatus {
    return this.evaluator.questStatus(questId);
  }

  /** A named quest's current display title, or `undefined` if it was never
   * given one - including a quest whose title only ever arrived via a
   * multiplayer `logic-snapshot` restore (`applyRemoteSnapshot`) rather than
   * this client's own locally-evaluated `start-quest` rule. */
  questTitle(questId: string): string | undefined {
    return this.evaluator.questTitle(questId);
  }

  /** The session's outcome once a win-game/lose-game rule has fired, else
   * null (still in progress). */
  outcome(): { result: 'win' | 'lose'; team?: string } | null {
    return this.evaluator.outcome();
  }

  /** Every effect produced so far this session, in order. */
  get log(): readonly LogicEffect[] {
    return this.effects;
  }

  /** Directly set a Logic variable's value, bypassing rule matching - for
   * the scripting `variables.set`/`variables.add` capability, which writes
   * without authoring a rule. Not on `ClientLogicRuntimeHandle`: the public
   * ref a host is handed stays read-only-shaped, this is for the scripting
   * capability layer, which holds the concrete runtime directly. */
  setVariable(name: string, value: number): void {
    this.evaluator.setVariable(name, value);
  }

  /** Run one Logic action directly, outside of rule matching, logging and
   * reporting its effect exactly like a rule-triggered action would - for
   * the scripting `logic` capability (`logic.announce`, `logic.winGame`,
   * etc.). Reserved ruleId `'__script'` so a script-triggered effect is
   * visibly distinct from an authored rule's own id in the effect log. Not
   * on `ClientLogicRuntimeHandle`, same reasoning as `setVariable` above.
   * Routed through `report()` exactly like a rule-matched effect, so a
   * script-triggered action (e.g. `logic.winGame()`) reaches `onSharedEffect`
   * too while this client is host - a HollowScript action must not bypass
   * sync just because it did not arrive via `emit()`. */
  applyAction(action: LogicAction): void {
    const effect = this.evaluator.applyAction(action, this.tickCount, SCRIPT_RULE_ID, this.localPlayerId);
    if (!effect) return;
    this.report(effect);
  }

  /** Run one Logic action sourced from another client over the relay,
   * outside of local rule matching - the `'__remote'` counterpart to
   * `applyAction`'s `'__script'` tag, so a non-host client's own effect log
   * can tell a relay-sourced effect apart from a rule-authored or
   * script-triggered one (2.6/3.2's extension point). `playerId` identifies
   * whose action this originally was (the remote player who produced it),
   * defaulting to this runtime's own local player id when the action has no
   * specific owner (e.g. a host-driven NPC's effect). The relay-listening
   * call site lives outside the engine (studio layer, Phase 3): a non-host
   * client converts each incoming `logic-effect` message's `LogicEffect`s
   * back into `LogicAction`s via `logicEffectToAction` (evaluator.ts) and
   * calls this once per action. */
  applyRemoteAction(action: LogicAction, playerId?: string): void {
    // Proof some other client's own round-start has already fired for this
    // session - see tick()'s own doc comment for why this must not gate on
    // `started`/the first tick alone. Set unconditionally, before the
    // no-op-effect early return below: even an action that produces no
    // effect (e.g. a variable write folded into a snapshot instead) is
    // still real evidence a host is running.
    this.roundStarted = true;
    const effect = this.evaluator.applyAction(action, this.tickCount, REMOTE_RULE_ID, playerId || this.localPlayerId);
    if (!effect) return;
    this.effects.push(effect);
    this.onEffect?.(effect);
  }

  /** Restore shared Logic state from a host's `logic-snapshot` message
   * (roadmap 3.4/3.5) - a non-host client's safety-net resync, covering a
   * client that joined mid-session (the relay's own late-join catch-up
   * frame, `sstk-studio-infra`'s `relay/src/index.js`) or missed a
   * `logic-effect` message. Deliberately silent, same reasoning as
   * `setVariable`: this is "make my state match the host's", not a
   * narrated event, so it produces no effects and does not touch `log`. A
   * `null` quest/variable delta is not possible (the snapshot always
   * carries the host's full current state, not a diff), so every key
   * present simply overwrites; nothing is ever removed by this call, since
   * a variable/quest a creator's document seeded before the host's own
   * evaluator ever produced state for it stays exactly what it was locally
   * until the host's own effects/snapshots say otherwise. */
  applyRemoteSnapshot(snapshot: RemoteLogicSnapshot): void {
    // See applyRemoteAction's own comment - the same reasoning applies here.
    this.roundStarted = true;
    for (const [name, value] of Object.entries(snapshot.variables)) this.evaluator.setVariable(name, value);
    for (const [questId, quest] of Object.entries(snapshot.quests)) this.evaluator.setQuestStatus(questId, quest.status, quest.title);
    for (const [playerId, items] of Object.entries(snapshot.items)) {
      for (const [name, count] of Object.entries(items)) this.evaluator.setItemCount(playerId, name, count);
    }
    this.evaluator.setOutcome(snapshot.outcome);
  }

  /** This session's full current shared-state snapshot, ready to broadcast
   * as a `logic-snapshot` message - a Logic host's own counterpart to
   * `applyRemoteSnapshot`. Not on `ClientLogicRuntimeHandle`, same
   * reasoning as `setVariable`: this is for the relay-broadcasting call
   * site (Phase 3, studio layer), not a host's own passive HUD reads. */
  remoteSnapshot(): RemoteLogicSnapshot {
    return {
      variables: this.evaluator.allVariables(),
      quests: this.evaluator.allQuestStatuses(),
      items: this.evaluator.allItemCounts(),
      outcome: this.evaluator.outcome(),
    };
  }

  private facts(): LogicFacts {
    // The generic host has no team/round-score concept of its own - see the
    // class doc comment. A creator's rules keying off
    // `score-raiders`/`score-wardens` simply read 0 here; that is the
    // correct, honest value for a host with no scoring lifecycle, not a
    // placeholder pretending to be real. `playerCount` is real, though - see
    // `setPlayerCount`/`ClientLogicRuntimeOptions.playerCount`.
    return { round: 1, scores: { raiders: 0, wardens: 0 }, playerCount: this.playerCount };
  }

  /** Effect production's single choke point: every effect this runtime ever
   * produces - rule-matched via `emit()` below (round-start, tick,
   * trigger-enter/exit, player-join/leave), or applied directly via
   * `applyAction` (the HollowScript `logic` capability) - passes through
   * here exactly once, so "does this effect leave this client and reach
   * every other player" is decided in one place rather than re-implemented,
   * inconsistently, at each call site.
   *
   * Unconditionally appends to `log` and calls `onEffect` (every effect,
   * host or not - unchanged from before this method existed), and
   * additionally calls `onSharedEffect` whenever this client `isHost` -
   * regardless of which caller produced the effect. This is deliberately
   * broader than the previous "only round-start/tick are shared" rule:
   * `round-start`/`tick` are already the only event kinds `tick()` ever
   * fires while `isHost` (so they only ever reach here as a host's own
   * effects anyway), but a host's own `trigger-enter`/`trigger-exit` and a
   * host's own script-triggered `applyAction` call produce effects exactly
   * as "belonging to the host" as those two do - and docs/multiplayer-sync/
   * roadmap.md's own Phase 5 creator-facing copy (`gameplayReadiness.ts`,
   * `docs/guide/multiplayer`) already promises every Logic effect a host's
   * rules produce reaches every other player, not only round-start/tick's.
   * A non-host client's own effects never leave this method's `onEffect`
   * call, exactly as before - `isHost` false is the same "stays local" gate
   * it always was, just centralised. */
  private report(effect: LogicEffect): void {
    this.effects.push(effect);
    this.onEffect?.(effect);
    if (this.isHost) this.onSharedEffect?.(effect);
  }

  private emit(kind: LogicEvent['kind'], extra: Partial<LogicEvent> = {}): void {
    const event: LogicEvent = { kind, tick: this.tickCount, playerId: this.localPlayerId, ...extra };
    const produced = this.evaluator.evaluate(event, this.facts());
    for (const effect of produced) this.report(effect);
  }

  /**
   * Advance one frame: `point`/`triggers` are the player's current position
   * and the map's trigger volumes for this tick. The first call also fires
   * the session's one-time `player-join` event (always, every client,
   * regardless of host status - personal, not shared). Also fires
   * `round-start` - see below for exactly when - and the periodic `'tick'`
   * Logic event, both host-only, `tick` at most once every
   * `TICK_EVENT_INTERVAL_MS` of wall-clock time regardless of how often
   * this method itself is called (once per rendered frame) - see
   * `TICK_EVENT_INTERVAL_MS`'s own doc comment.
   *
   * `round-start` is genuinely once-per-session, not once-per-first-tick
   * and not once-per-host - both of those are real distinctions a
   * multiplayer session needs and a single-player one does not:
   *
   * - Not once-per-first-tick: `isHost` starts `false` for every client in
   *   a real multiplayer session (until relay election actually resolves -
   *   see `ClientLogicRuntimeOptions.isHost`'s own doc comment for why it
   *   must), and this method's *first* call always happens before that
   *   resolves (rendering starts on mount; the relay connects
   *   asynchronously after). Gating `round-start` on `!this.started` (the
   *   same one-shot guard `player-join` uses) would consume its one
   *   chance to fire before any client's host status is even known -
   *   `round-start` would then never fire for anyone, ever, in any
   *   multiplayer session. It is gated on its own separate
   *   `roundStarted` flag instead, checked (and possibly fired) on
   *   *every* tick until it succeeds - so whichever tick first finds
   *   `isHost` true, whether that is the very first one (single-player,
   *   `isHost` defaults `true`) or a later one (multiplayer, once
   *   election resolves), is the one that fires it.
   * - Not once-per-host: a client promoted to host *mid-session* by a
   *   host migration (roadmap 3.2 item 4) must not re-fire `round-start`
   *   even once it starts firing periodic `tick` events - re-running "the
   *   round started" rules mid-session on a handoff (re-granting starting
   *   items, restarting a timer) would be a real bug, not a recovery. This
   *   is what `roundStarted` alone (a per-instance flag) would get wrong
   *   for a client that was never host before its promotion - so
   *   `applyRemoteAction`/`applyRemoteSnapshot` also mark `roundStarted`
   *   true the moment either is ever called: receiving *any* remote
   *   shared-state broadcast is proof some other client's own `round-start`
   *   has already fired for this session, whether or not this client ever
   *   becomes host itself. The new host then continues authoritatively
   *   from whatever shared state it already has (its own last-received
   *   `logic-snapshot` from the old host, or - if migration happened
   *   before any snapshot ever arrived - the still-unsynced defaults
   *   every non-host client starts with); this is the same accepted,
   *   documented cost the roadmap's own migration section already names
   *   ("the new host's own local copy becomes authoritative going
   *   forward"), not a gap this method introduces.
   */
  tick(point: ClientLogicTickPoint, triggers: readonly MapTriggerVolume[]): void {
    this.tickCount += 1;
    if (!this.started) {
      this.started = true;
      this.lastTickEventAt = Date.now();
      this.emit('player-join');
    }
    if (!this.roundStarted && this.isHost) {
      this.roundStarted = true;
      this.emit('round-start');
    }
    const now = Date.now();
    if (now - this.lastTickEventAt >= TICK_EVENT_INTERVAL_MS) {
      this.lastTickEventAt = now;
      if (this.isHost) this.emit('tick');
    }

    const { entered, exited } = diffTriggerContainment(point, triggers, this.insideIds);
    const ctx: TriggerContext = { playerX: point.x, playerZ: point.z, playerFloor: point.floor };

    for (const volume of exited) {
      dispatchTrigger(volume, ctx);
      this.emit('trigger-exit', { trigger: triggerLabel(volume, triggers) });
    }
    for (const volume of entered) {
      dispatchTrigger(volume, ctx);
      this.emit('trigger-enter', { trigger: triggerLabel(volume, triggers) });
    }
  }

  /** Fires `player-leave` once, for a host that wants an honest end-of-session
   * event (e.g. on unmount). Safe to call even if the session never started
   * (a no-op, since there was never a join). */
  end(): void {
    if (!this.started) return;
    this.emit('player-leave');
  }
}
